
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { users } from '@/core/realisations/users.realisation'
import { billing } from '@/core/realisations/billing.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useList } from '@/common/composable/useList'

// validations
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

// types
import { UserT } from '@/core/types/users.types'

import {
    CompanyT,
    StageT,
    PeriodT,
    WeightTypeT,
    CompanySetBillingPayloadT,
    CompanyBillingOrderType,
    CompanyBillingTranscriptType,
} from '@/core/types/companies.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { BILLING_TARIFF_TYPES_EXTENDED } from '@/core/constants/billing.constants'

const WEIGHTS = [
    { id: 10, name: '10' },
    { id: 25, name: '25' },
    { id: 50, name: '50' },
    { id: 100, name: '100' }
]

export default defineComponent({
    components: {
        Modal,
        TheSelect,
        Checkbox,
        TheButton,
    },
    props: {
        company: {
            type: [null, Object] as PropType<null | CompanyT>,
            default: null,
        },
    },
    emits: ['cancel', 'submit'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { loadList, loadListMore } = useList()

        const state = reactive({
            is_loading: false,
            //
            vatEnabled: 0,
            instantFtl: 0,
        })

        const payload_state = reactive({
            accountantId: null as number | null,
            billingPaymentStageId: null as number | null,
            billingPeriodId: null as number | null,
            billingWeightTypeId: null as number | null,
            billingEnabled: false,
            billingShipmentTariffId: null as number | null,
            billingOrderTypeId: null as number | null,
            billingTranscriptTypeId: null as number | null  
        })

        const new_standard_tariff_state = reactive({
            is_loading: false,
            is_created: false,
            weight: null as number | null
        })
        
        const data_state = reactive({
            accountants: new DataState<UserT>({}),
            stages: new DataState<StageT>({}),
            periods: new DataState<PeriodT>({}),
            weight_types: new DataState<WeightTypeT>({}),
        })

        const order_types = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CompanyBillingOrderType[],
        })

        const transcript_types = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CompanyBillingTranscriptType[],
        })

        const new_standard_tariff_state$ = useVuelidate(
            {
                weight: { required: requiredIf(() => payload_state.billingShipmentTariffId === 7) }
            },
            new_standard_tariff_state
        )

        const state_form$ = useVuelidate(
            {
                billingPaymentStageId: { required },
                billingWeightTypeId: { required },
            },
            payload_state
        )

        const emitCancel = () => {
            emit('cancel')
        }

        // accountants

        const loadAccountants = async (page: number, name?: string) => {
            data_state.accountants.is_loading = true

            const response = await users.getAccountants(page, name)
            loadList({ data_state: data_state.accountants, response, page })
        }

        const loadMoreAccountants = async (page: number) => {
            if (page > data_state.accountants.pagination.total_pages) return

            const response = await users.getAccountants(page)
            loadListMore({ data_state: data_state.accountants, response, page })
        }

        // stages

        const loadStages = async (page: number) => {
            data_state.stages.is_loading = true

            const response = await companies.getStages(page)
            loadList({ data_state: data_state.stages, response, page })
        }

        const loadMoreStages = async (page: number) => {
            if (page > data_state.stages.pagination.total_pages) return

            const response = await companies.getStages(page)
            loadListMore({ data_state: data_state.stages, response, page })
        }

        // periods

        const loadPeriods = async (page: number) => {
            data_state.periods.is_loading = true

            const response = await companies.getPeriods(page)
            loadList({ data_state: data_state.periods, response, page })
        }

        const loadMorePeriods = async (page: number) => {
            if (page > data_state.periods.pagination.total_pages) return

            const response = await companies.getPeriods(page)
            loadListMore({ data_state: data_state.periods, response, page })
        }

        // weight_types

        const loadWeightTypes = async (page: number) => {
            data_state.weight_types.is_loading = true

            const response = await companies.getWeightTypes(page)
            loadList({ data_state: data_state.weight_types, response, page })
        }

        const loadMoreWeightTypes = async (page: number) => {
            if (page > data_state.weight_types.pagination.total_pages) return

            const response = await companies.getWeightTypes(page)
            loadListMore({ data_state: data_state.weight_types, response, page })
        }

        // order_types

        const loadOrderTypes = async () => {
            try {
                order_types.is_loading = true

                const response = await companies.getOrderTypes()
                order_types.list = response.data

                order_types.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении типов заказа произошла ошибка',
                })
                order_types.is_failed = true
            } finally {
                order_types.is_loading = false
            }
        }

        const loadTranscriptTypes = async () => {
            try {
                transcript_types.is_loading = true

                const response = await companies.getTranscriptTypes()
                transcript_types.list = response.data

                transcript_types.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении типов транскрипта произошла ошибка',
                })
                transcript_types.is_failed = true
            } finally {
                transcript_types.is_loading = false
            }
        }

        const submit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || !props.company) return

            try {
                state.is_loading = true

                const payload = { ...payload_state }

                await companies.setBilling(props.company.id, payload as CompanySetBillingPayloadT)

                notifications.notify({
                    title: 'Настройки биллинга компании успешно изменены!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При настройке биллинга произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }

            try {
                state.is_loading = true

                await billing.setVat(props.company.id, {
                    vatEnabled: state.vatEnabled,
                    instantFtl: state.instantFtl,
                })

                notifications.notify({
                    title: 'Параметр ндс успешно обновлен!',
                    type: 'success',
                })

                emit('submit')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При настройке биллинга произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const setInfo = () => {
            if (!props.company) return

            payload_state.accountantId = props.company.accountant
                ? props.company.accountant.id
                : null
            payload_state.billingEnabled = props.company.billing_enabled
            payload_state.billingPaymentStageId = props.company.billing_payment_stage
                ? props.company.billing_payment_stage.id
                : null
            payload_state.billingPeriodId = props.company.billing_period
                ? props.company.billing_period.id
                : null
            payload_state.billingWeightTypeId = props.company.billing_weight_type
                ? props.company.billing_weight_type.id
                : null
            payload_state.billingShipmentTariffId = props.company.shipment_tariff_id

            if (props.company.billing_order_type) {
                payload_state.billingOrderTypeId = props.company.billing_order_type.id
            }

            if (props.company.billing_transcript_type) {
                payload_state.billingTranscriptTypeId = props.company.billing_transcript_type.id
            }
        }

        const loadCompany = async () => {
            if (!props.company) return

            try {
                state.is_loading = true

                const response = await billing.getCompany(props.company.id)

                if (!response.data) return

                state.instantFtl = response.data.instantFtl
                state.vatEnabled = response.data.vatEnabled
            } catch (error) {
                console.log(error)
            } finally {
                state.is_loading = false
            }
        }

        const onTariffChange = () => {
            new_standard_tariff_state.weight = null
        }

        const createNewStandardTariff = async () => {
            if (new_standard_tariff_state.is_loading || !props.company) return

            new_standard_tariff_state$.value.$touch()
            if (new_standard_tariff_state$.value.$invalid) return

            try {
                new_standard_tariff_state.is_loading = true

                await billing.createDefaultTariffV3({ weight: new_standard_tariff_state.weight as number, companyId: props.company.id })

                new_standard_tariff_state.is_created = true
                notifications.notify({ type: 'success', title: 'Тариф создан', description: 'Тариф изменен со старых параметров на новый' })
                emit('submit')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании тарифа произошла ошибка' })
            } finally {
                new_standard_tariff_state.is_loading = false
            }
        }

        const isLoading = computed(() => {
            return (
                data_state.accountants.is_loading ||
                data_state.stages.is_loading ||
                data_state.periods.is_loading ||
                data_state.weight_types.is_loading ||
                state.is_loading
            )
        })

        //

        onBeforeMount(async () => {
            loadAccountants(1)
            loadStages(1)
            loadPeriods(1)
            loadWeightTypes(1)
            loadOrderTypes()
            loadTranscriptTypes()

            await loadCompany()

            setInfo()
        })

        //

        return {
            state,
            payload_state,
            data_state,
            order_types,
            transcript_types,
            state_form$,
            emitCancel,
            submit,
            // accountants
            loadAccountants,
            loadMoreAccountants,
            // stages
            loadStages,
            loadMoreStages,
            // periods
            loadPeriods,
            loadMorePeriods,
            // types
            loadWeightTypes,
            loadMoreWeightTypes,
            //
            loadOrderTypes,
            loadTranscriptTypes,
            //
            isLoading,
            //
            new_standard_tariff_state,
            new_standard_tariff_state$,
            onTariffChange,
            createNewStandardTariff,
            // constants
            WEIGHTS,
            BILLING_TARIFF_TYPES_EXTENDED,
        }
    },
})
